import { defer } from "@tanstack/react-router";
import { fetchAllCoolingRegimePriorities } from "../services/coolingRegimePriorityService";
import {
  mapCoolingRegimePrioritiesToRows,
  type PriorityRow,
} from "../mappers/coolingRegimePriorityMapper";

export interface CoolingRegimePriorityFilters {
  priorityId?: string;
  description?: string;
  vbnChangeDate?: string;
  modifiedDate?: string;
  modifiedBy?: string;
}

export interface CoolingRegimePriorityLoaderResult {
  rows: PriorityRow[];
  totalCount: number;
  error?: string;
}

/**
 * Loads cooling regime priorities with filters applied.
 * @param filters - Filter criteria
 * @returns Deferred promise with priority rows
 */
export function createCoolingRegimePriorityDeferredLoader(
  filters: CoolingRegimePriorityFilters,
) {
  const dataPromise = loadCoolingRegimePriorities(filters);
  return { data: defer(dataPromise) };
}

async function loadCoolingRegimePriorities(
  filters: CoolingRegimePriorityFilters,
): Promise<CoolingRegimePriorityLoaderResult> {
  try {
    const priorities = await fetchAllCoolingRegimePriorities();
    let filteredPriorities = priorities;

    // Apply filters
    if (filters.priorityId) {
      const id = parseInt(filters.priorityId);
      filteredPriorities = filteredPriorities.filter(
        (p) => p.coolingRegimePriorityId === id,
      );
    }

    if (filters.description) {
      const searchTerm = filters.description.toLowerCase();
      filteredPriorities = filteredPriorities.filter((p) =>
        p.description?.toLowerCase().includes(searchTerm),
      );
    }

    if (filters.modifiedDate) {
      const filterDate = filters.modifiedDate;
      filteredPriorities = filteredPriorities.filter((p) => {
        if (!p.lastModified) return false;
        const dateStr = new Date(p.lastModified).toISOString().split("T")[0];
        return dateStr === filterDate;
      });
    }

    if (filters.modifiedBy) {
      const searchTerm = filters.modifiedBy.toLowerCase();
      filteredPriorities = filteredPriorities.filter((p) =>
        p.lastModifiedBy?.toLowerCase().includes(searchTerm),
      );
    }

    const rows = mapCoolingRegimePrioritiesToRows(filteredPriorities);

    return {
      rows,
      totalCount: rows.length,
    };
  } catch (error) {
    console.error("Error loading cooling regime priorities:", error);
    return {
      rows: [],
      totalCount: 0,
      error:
        error instanceof Error
          ? error.message
          : "Failed to load cooling regime priorities",
    };
  }
}
